import {
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
} from 'react';
import styles from './Radio.module.css';

export type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  label?: ReactNode;
  /** Пояснение под подписью — приглушённым тоном (как у варианта «Статусы задач» дубликата). */
  description?: ReactNode;
  /** Значок перед подписью (вид задачи: обычная / группа / веха). */
  leadingIcon?: ReactNode;
};

/**
 * F103 пака `tails-closeout` (С-I, TPL-F14; SOLUTION § 10.6 п. 7, К5-18) — примитив радиокнопки.
 *
 * Обёртка над нативным `<input type="radio">` с `appearance: none`: стрелки, группировка по `name`,
 * `disabled` и порядок Tab остаются нативными — `role="radio"` не переизобретается. Нативная
 * радиокнопка в светлой теме выглядела закрашенной рядом с флажками и красилась цветом браузера;
 * образец — радио диалогов шаблонов (F61, F100): невыбранная без заливки, кольцо `--text-muted`
 * (5.97 / 5.29 к фону диалога), выбранная — точка `--color-primary`, тем же тоном, что отмеченный
 * `Checkbox`. Сторож `scripts/lint/fe-no-native-radio.mjs`: `type="radio"` в JSX вне примитива — красный.
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, description, leadingIcon, id, className, disabled, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasText = label !== undefined || description !== undefined || leadingIcon !== undefined;

  return (
    <label
      htmlFor={inputId}
      className={[styles.wrap, disabled ? styles.wrapDisabled : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      <input
        ref={ref}
        id={inputId}
        type="radio"
        className={styles.input}
        disabled={disabled}
        {...rest}
      />
      {hasText && (
        <span className={styles.text}>
          <span className={styles.label}>
            {leadingIcon && (
              <span className={styles.icon} aria-hidden="true">
                {leadingIcon}
              </span>
            )}
            {label}
          </span>
          {description !== undefined && <span className={styles.description}>{description}</span>}
        </span>
      )}
    </label>
  );
});

export type RadioGroupProps = HTMLAttributes<HTMLDivElement> & {
  orientation?: 'vertical' | 'horizontal';
  children: ReactNode;
};

/**
 * Группа радиокнопок: `role="radiogroup"` и шаг между вариантами (горизонтально — `--space-4`, больше
 * зазора «кружок — подпись»). Имя группы — `aria-labelledby` на видимую подпись или `aria-label`.
 */
export function RadioGroup({
  orientation = 'vertical',
  className,
  children,
  ...rest
}: RadioGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-orientation={orientation}
      className={[
        styles.group,
        orientation === 'horizontal' ? styles.groupHorizontal : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}
